/**
 * OversightSettingsCard — AG-08 per-tenant human-oversight switches, rendered
 * inside the Safety panel. Both defaults reproduce the pre-existing behaviour
 * (approval gate off, AI auto-reply on).
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import { UserCheck } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import RequirePermission from '../../auth/RequirePermission.js'

interface OversightSettings {
  requireApprovalForWriteTools: boolean
  autoReplyEnabled: boolean
}

const URL = '/admin/ai/governance/oversight-settings'

export default function OversightSettingsCard(): JSX.Element | null {
  const toast = useToast()
  const [settings, setSettings] = useState<OversightSettings | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setSettings(await api.get<OversightSettings>(URL))
    } catch {
      // Viewer without ai:governance_view - the card simply does not render.
      setSettings(null)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function update(patch: Partial<OversightSettings>, label: string): Promise<void> {
    setSaving(true)
    try {
      setSettings(await api.put<OversightSettings>(URL, patch))
      toast.success('Oversight setting saved', label)
    } catch (err) {
      toast.error('Failed to save oversight setting', String(err))
    } finally {
      setSaving(false)
    }
  }

  if (!settings) return null

  const rows: Array<{ key: keyof OversightSettings; title: string; help: string }> = [
    {
      key: 'requireApprovalForWriteTools',
      title: 'Require approval for AI write actions',
      help: 'When on, the AI assistant cannot assign conversations, campaigns or branches, change conversation properties or set priority until a reviewer with approval rights (not the requester) approves. The approved action then runs once. Off by default.',
    },
    {
      key: 'autoReplyEnabled',
      title: 'AI auto-replies enabled',
      help: 'Kill switch. Turn off to stop all AI auto-replies for this tenant immediately. On by default.',
    },
  ]

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
      <div className="flex items-center gap-2">
        <UserCheck className="h-4 w-4 text-[#3454cf]" />
        <h4 className="text-xs font-semibold text-gray-900">Human Oversight</h4>
      </div>
      <div className="mt-3 divide-y divide-gray-100">
        {rows.map((r) => (
          <div key={r.key} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
            <div>
              <p className="text-xs font-medium text-gray-900">{r.title}</p>
              <p className="mt-0.5 text-[11px] text-gray-500">{r.help}</p>
            </div>
            <RequirePermission
              permission="ai:governance_manage"
              fallback={
                <span className="text-xs text-gray-500">{settings[r.key] ? 'On' : 'Off'}</span>
              }
            >
              <input
                type="checkbox"
                role="switch"
                aria-label={r.title}
                className="mt-1 h-4 w-4 shrink-0 accent-[#3454cf]"
                checked={settings[r.key]}
                disabled={saving}
                onChange={(e) =>
                  void update({ [r.key]: e.target.checked }, `${r.title}: ${e.target.checked ? 'on' : 'off'}`)
                }
              />
            </RequirePermission>
          </div>
        ))}
      </div>
    </div>
  )
}
